import { fetchMfaStatus, useAuthentication, useQueryParam, validateRedirect } from '@psyomics/ui-common';
import { useMemo, useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { REFERRAL_REDIRECT_WHITELIST } from '../../redirect-whitelists';

type MfaStatus = {
  mfaIsComplete: boolean | null;
  error: Error | null;
  isLoadingMfaStatus: boolean;
  hasResolved: boolean;
};

const useMfaStatus = ({
  apiUrl,
  isAuthenticated,
  isLoadingAuth,
  isMfaSetupRoute,
}: {
  apiUrl: string;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  isMfaSetupRoute: boolean;
}): MfaStatus => {
  const [mfaIsComplete, setMfaIsComplete] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoadingMfaStatus, setIsLoadingMfaStatus] = useState(true);
  const [hasResolved, setHasResolved] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isLoadingAuth) return;

    const isInitialFetch = !hasResolved;
    setIsLoadingMfaStatus(isInitialFetch);
    setError(null);

    fetchMfaStatus(apiUrl)
      .then((response) => {
        setMfaIsComplete(response.mfaComplete);
        setHasResolved(true);
        setIsLoadingMfaStatus(false);
      })
      .catch((err) => {
        setError(new Error(String(err)));
        setIsLoadingMfaStatus(false);
      });
  }, [apiUrl, hasResolved, isAuthenticated, isLoadingAuth, isMfaSetupRoute]);

  return { error, hasResolved, isLoadingMfaStatus, mfaIsComplete };
};

const getSetupUrl = (redirect: string) =>
  `/mfa-setup?${new URLSearchParams({ redirect, 'require-phone': '' }).toString()}`;

const getRedirectFromMfa = (redirectParam?: string): string => {
  const validatedRedirect = validateRedirect(redirectParam, REFERRAL_REDIRECT_WHITELIST);
  if (!validatedRedirect || validatedRedirect.startsWith('/mfa-setup')) return '/';
  return validatedRedirect;
};

const getMfaRedirect = ({
  mfaIsComplete,
  isMfaSetupRoute,
  pathname,
  redirectParam,
}: {
  mfaIsComplete: boolean;
  isMfaSetupRoute: boolean;
  pathname: string;
  redirectParam?: string;
}) => {
  if (mfaIsComplete && isMfaSetupRoute) return getRedirectFromMfa(redirectParam);
  if (!mfaIsComplete && !isMfaSetupRoute) return getSetupUrl(pathname);
  return null;
};

const useMfaBlockingCheck = ({
  hasResolved,
  isLoadingAuth,
  mfaIsComplete,
  isLoadingMfaStatus,
}: Omit<MfaStatus, 'error'> & { isLoadingAuth: boolean }) =>
  useMemo(
    () => isLoadingAuth || (!hasResolved && isLoadingMfaStatus) || mfaIsComplete === null,
    [hasResolved, isLoadingAuth, isLoadingMfaStatus, mfaIsComplete]
  );

const hasResolvedMfaStatus = (mfaStatus: MfaStatus): mfaStatus is MfaStatus & { mfaIsComplete: boolean } =>
  mfaStatus.mfaIsComplete !== null;

const RequireMfaSetup = () => {
  const { apiUrl, isAuthenticated, isLoading: isLoadingAuth } = useAuthentication();
  const { pathname } = useLocation();
  const redirectParam = useQueryParam('redirect');
  const isMfaSetupRoute = pathname === '/mfa-setup';
  const mfaStatus = useMfaStatus({ apiUrl, isAuthenticated, isLoadingAuth, isMfaSetupRoute });
  const isGuardBlocked = useMfaBlockingCheck({
    ...mfaStatus,
    isLoadingAuth,
  });

  const redirectTo = useMemo(() => {
    if (mfaStatus.error) return null;
    if (!hasResolvedMfaStatus(mfaStatus)) return null;
    if (isGuardBlocked) return null;

    return getMfaRedirect({
      isMfaSetupRoute,
      mfaIsComplete: mfaStatus.mfaIsComplete,
      pathname,
      redirectParam,
    });
  }, [mfaStatus, isGuardBlocked, isMfaSetupRoute, pathname, redirectParam]);

  if (!isLoadingAuth && !isAuthenticated) throw new Error('not authenticated');
  if (mfaStatus.error) throw mfaStatus.error;
  if (isGuardBlocked) return null;
  if (redirectTo) {
    return <Navigate replace to={redirectTo} />;
  }
  return <Outlet />;
};

export { RequireMfaSetup };
